// @givmo/compliance — sending a filing yourself: the agency's instructions, and the organization's
// own record of what it sent (CCM-R1-SUBMIT-HANDOFF; UX slice 3 §10, frames 79–81, approved under
// D-CCM-27).
//
// The send page's second face, at the same address and under the same title. It shows where Givmo
// won't email the filing (a fee, D-CCM-26; or no email channel), where the organization asked for it
// ("Send it yourself instead"), and once the organization recorded what it sent.
//
// ══ WHAT THE PAGE SAYS, AND WHOSE WORDS THEY ARE ═══════════════════════════════════════════════
//
// Four steps, numbered as they are shown (MH1): get the package, add the fee (only with one), send
// it to the agency, record what you sent. Every sentence about the agency is the agency's own,
// quoted with where it is printed: the form's mail address and email line (each only where it IS
// the rule's destination, a tie the backend makes), how the fee is paid, who signs. Online, the
// sources' cited note and the portal's name and address. Nothing here is Givmo's reading of the
// law: the instructions are transcribed, never advised. For a filing Givmo can email, reached by
// "Send it yourself instead", step 2 ends with the way back, "Have Givmo email it instead" (N-H1,
// D-CCM-40): it opens the review, where nothing is emailed until someone authorizes it.
//
// ⚠ THE RECORD IS THE ORGANIZATION'S, NEVER GIVMO'S. Recording what was sent sends nothing; the step
// says so, and the Sent line reads "recorded by". Nothing claims delivery, receipt or acceptance.

import { useState, type FormEvent, type ReactNode } from 'react';
import {
  Button,
  Card,
  ErrorMessage,
  Icon,
  IconButton,
  RadioTile,
  RadioTileGroup,
  testIdProp,
} from '@givmo/design-system';
import { Download, FileText, X } from 'lucide-react';
import { ArtifactUpload } from './artifact-upload';
import { displayArtifactSize, serverMessage, type ComplianceArtifact } from './compliance-decode';
import { ComplianceDateField } from './compliance-date-field';
import { ComplianceDisclosure } from './compliance-disclosure';
import { STICKY_COLUMN_CLASSES } from './compliance-shared';
import { moreNotNeededLine } from './compliance-signing-model';
import type {
  HandoffContent,
  SubmissionChannel,
  SubmissionChannelInstruction,
  SubmissionFeeStatement,
  SubmissionHandoff,
  SubmissionInstructions,
} from './compliance-submission-decode';
import {
  ADD_THE_FEE,
  ATTACH,
  CANCEL,
  CHANNEL_HEADINGS,
  DATE_SENT,
  DOWNLOAD_THE_PACKAGE,
  GET_THE_PACKAGE,
  HERE_IS_HOW,
  HOW_YOU_SENT_IT,
  PROOF_OF_SENDING_OPTIONAL,
  RECORD_WHAT_YOU_SENT,
  RECORD_WHAT_YOU_SENT_STEP,
  SAVE,
  SOMETHING_DIFFERENT,
  WHAT_YOU_SENT,
  WHO_SIGNS,
  emailWithFeeLine,
  feeEnteredLine,
  feePaymentLine,
  formSaysLine,
  formSourceLine,
  handoffPackageLead,
  handoffRecordedLine,
  onlineChoiceLabel,
  packageAboveLabel,
  proofHintLine,
  recordStepLine,
  sendItToStep,
  signedWithCapacityLine,
} from './compliance-submission-model';
import { PackageList, SourceLine, type ShownFile } from './filing-send-parts';

const PREFIX = 'compliance-handoff';

/** The line a failed record says when the server gave none (the module's own). */
const TRY_AGAIN_LINE = 'Please try again.';

/**
 * The page's order of the ways (frame 79): with a fee, mail, then online, then email (which the form
 * doesn't say a fee is paid by); with none, email first (MH3). The record form lists the same ways,
 * in the same order (MH5): the ones the rule records that have an approved label.
 */
function waysInOrder(withFee: boolean): readonly SubmissionChannel[] {
  return withFee
    ? ['postal_mail', 'online_portal', 'email']
    : ['email', 'postal_mail', 'online_portal'];
}

// ── The parts ──────────────────────────────────────────────────────────────────────────────────

/** The lead (frame 79): the served reason Givmo won't email it, then how to send it yourself. */
export function HandoffLead({ reason }: { reason: string }) {
  return (
    <div
      className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-surface px-16 py-12 text-s14 leading-h15 text-text"
      {...testIdProp(`${PREFIX}-lead`)}
    >
      <p>{reason}</p>
      <p>{HERE_IS_HOW}</p>
    </div>
  );
}

/** One numbered step: its number quiet beside its title (frame 79). */
function Step({
  number,
  title,
  children,
  testId,
}: {
  number: number;
  title: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section className="flex flex-col gap-10" {...testIdProp(testId)}>
      <h2 className="text-s16 font-w600 text-text-strong">
        <span className="text-text-tertiary">{number}.</span> {title}
      </h2>
      {children}
    </section>
  );
}

/** "Who signs" (frame 79): who signed and as what, the form's own words, the factual line. */
export interface HandoffSigner {
  key: string;
  /** The roster's name, or undefined where it can't name them: the line says who (D-CCM-99). */
  name: string | undefined;
  day: string;
  capacity: string;
}

function WhoSigns({
  signers,
  jurisdiction,
  signerQuote,
  factualLine,
}: {
  signers: readonly HandoffSigner[];
  jurisdiction: string;
  signerQuote: string | undefined;
  factualLine: string | undefined;
}) {
  if (signers.length === 0 && signerQuote === undefined && factualLine === undefined) return null;
  return (
    <div
      className="flex flex-col gap-6 rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12"
      {...testIdProp(`${PREFIX}-who-signs`)}
    >
      <p className="text-s13 font-w600 text-text-strong">{WHO_SIGNS}</p>
      {signers.map((signer) => (
        <p key={signer.key} className="text-s13 leading-h15 text-text">
          {signedWithCapacityLine(signer.name, signer.day, signer.capacity)}
        </p>
      ))}
      {signerQuote !== undefined && (
        <SourceLine testId={`${PREFIX}-signer-quote`}>
          {formSaysLine(jurisdiction, signerQuote)}
        </SourceLine>
      )}
      {factualLine !== undefined && (
        <p className="text-s13 leading-h15 text-text" {...testIdProp(`${PREFIX}-factual-line`)}>
          {factualLine}
        </p>
      )}
    </div>
  );
}

/** One way to send it, as a row of step 3's card. */
function ChannelRow({
  heading,
  children,
  testId,
}: {
  heading: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <li className="flex flex-col gap-6 px-20 py-14" {...testIdProp(testId)}>
      <p className="text-s14 font-w600 text-text-strong">{heading}</p>
      {children}
    </li>
  );
}

function PortalLink({ channel }: { channel: SubmissionChannelInstruction }) {
  if (channel.portalUrl === undefined) return null;
  return (
    <a
      href={channel.portalUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="self-start text-s13 text-text underline underline-offset-2 hover:text-text-secondary"
      {...testIdProp(`${PREFIX}-portal`)}
    >
      {channel.portalName ?? channel.portalUrl}{' '}
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}

/**
 * Step 3's card (frame 79): each way the rule records, in the page's order. With a fee, mail first
 * and email last with its line that the form doesn't say how the fee is paid that way; with none,
 * email first, quoting the form's own email line (MH3).
 */
function Channels({
  instructions,
  jurisdiction,
  withFee,
}: {
  instructions: SubmissionInstructions;
  jurisdiction: string;
  withFee: boolean;
}) {
  const checkedOn = instructions.formSource?.checkedOn;
  const ordered = waysInOrder(withFee).flatMap((way) => {
    const channel = instructions.channels.find((candidate) => candidate.channel === way);
    return channel !== undefined ? [channel] : [];
  });
  if (ordered.length === 0) return null;
  return (
    <Card className="overflow-hidden" testId={`${PREFIX}-channels`}>
      <ul className="divide-y divide-border-subtle">
        {ordered.map((channel) => {
          const heading =
            channel.channel !== 'unknown' ? (CHANNEL_HEADINGS[channel.channel] ?? '') : '';
          if (channel.channel === 'postal_mail') {
            const lines =
              channel.quote !== undefined
                ? channel.quote.text.split('\n').filter((line) => line.trim().length > 0)
                : channel.address !== undefined
                  ? [channel.address]
                  : [];
            return (
              <ChannelRow key="postal_mail" heading={heading} testId={`${PREFIX}-mail`}>
                <p className="text-s14 leading-h15 text-text">
                  {lines.map((line, index) => (
                    <span key={index} className="block">
                      {line}
                    </span>
                  ))}
                </p>
                {channel.quote !== undefined && (
                  <SourceLine testId={`${PREFIX}-mail-source`}>
                    {formSourceLine(jurisdiction, channel.quote.page, checkedOn)}
                  </SourceLine>
                )}
              </ChannelRow>
            );
          }
          if (channel.channel === 'online_portal') {
            return (
              <ChannelRow key="online_portal" heading={heading} testId={`${PREFIX}-online`}>
                {channel.note !== undefined && (
                  <p className="text-s13 leading-h15 text-text">{channel.note}</p>
                )}
                <PortalLink channel={channel} />
              </ChannelRow>
            );
          }
          // By email.
          return (
            <ChannelRow key="email" heading={heading} testId={`${PREFIX}-email`}>
              {withFee ? (
                channel.address !== undefined && (
                  <p className="text-s13 leading-h15 text-text">
                    {emailWithFeeLine(jurisdiction, channel.address)}
                  </p>
                )
              ) : channel.quote !== undefined ? (
                <>
                  <p className="text-s13 leading-h15 text-text">{channel.quote.text}</p>
                  <SourceLine testId={`${PREFIX}-email-source`}>
                    {formSourceLine(jurisdiction, channel.quote.page, checkedOn)}
                  </SourceLine>
                </>
              ) : (
                channel.address !== undefined && (
                  <p className="text-s13 leading-h15 text-text">{channel.address}</p>
                )
              )}
            </ChannelRow>
          );
        })}
      </ul>
    </Card>
  );
}

/** A document attached to the record: the vault's upload, then its chip with its size. */
function AttachedDocument({
  artifact,
  onChange,
  testId,
}: {
  artifact: ComplianceArtifact | undefined;
  onChange: (artifact: ComplianceArtifact | undefined) => void;
  testId: string;
}) {
  if (artifact === undefined) {
    return <ArtifactUpload label={ATTACH} testId={testId} onUploaded={(next) => onChange(next)} />;
  }
  return (
    <div
      className="flex w-full items-center gap-10 rounded-md border border-border-subtle px-12 py-10"
      {...testIdProp(`${testId}-attached`)}
    >
      <Icon icon={FileText} size={16} className="shrink-0 text-icon-muted" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-s14 text-text">{artifact.fileName}</span>
      {artifact.byteSize !== undefined && (
        <span className="shrink-0 text-s12 text-text-tertiary">
          {displayArtifactSize(artifact.byteSize)}
        </span>
      )}
      <IconButton
        size="sm"
        tone="ghost"
        aria-label={`Remove ${artifact.fileName}`}
        onClick={() => onChange(undefined)}
      >
        <Icon icon={X} size={14} aria-hidden />
      </IconButton>
    </div>
  );
}

/** What the record form sends: the wire's own body. */
export interface HandoffRecordBody {
  channel: SubmissionChannel;
  sent_on: string;
  content: HandoffContent;
  package_sha256?: string;
  sent_artifact_id?: string;
  proof_artifact_id?: string;
}

/** Frame 80's form, inline in step 4's card: how, when, what, and an optional proof. */
function RecordForm({
  instructions,
  withFee,
  fileCount,
  packageSha256,
  pending,
  failure,
  onSave,
  onCancel,
}: {
  instructions: SubmissionInstructions;
  withFee: boolean;
  fileCount: number;
  packageSha256: string | undefined;
  pending: boolean;
  failure: unknown;
  onSave: (body: HandoffRecordBody) => void;
  onCancel: () => void;
}) {
  const ways = waysInOrder(withFee).flatMap((way) => {
    const channel = instructions.channels.find((candidate) => candidate.channel === way);
    if (channel === undefined) return [];
    const label =
      way === 'online_portal' ? onlineChoiceLabel(channel.portalName) : CHANNEL_HEADINGS[way];
    return label !== undefined ? [{ way, label }] : [];
  });
  const portalName = instructions.channels.find(
    (channel) => channel.channel === 'online_portal',
  )?.portalName;
  // Nothing is chosen for the person but "The package above" (frame 80's default).
  const [way, setWay] = useState<SubmissionChannel | ''>('');
  const [sentOn, setSentOn] = useState('');
  const [content, setContent] = useState<HandoffContent>(
    packageSha256 !== undefined ? 'package' : 'other',
  );
  const [sent, setSent] = useState<ComplianceArtifact | undefined>(undefined);
  const [proof, setProof] = useState<ComplianceArtifact | undefined>(undefined);

  const ready =
    way !== '' &&
    sentOn !== '' &&
    (content === 'package' ? packageSha256 !== undefined : sent !== undefined);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    onSave({
      channel: way,
      sent_on: sentOn,
      content,
      ...(content === 'package' && packageSha256 !== undefined
        ? { package_sha256: packageSha256 }
        : {}),
      ...(content === 'other' && sent !== undefined ? { sent_artifact_id: sent.artifactId } : {}),
      ...(proof !== undefined ? { proof_artifact_id: proof.artifactId } : {}),
    });
  };

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-16 border-t border-border-subtle pt-16"
      {...testIdProp(`${PREFIX}-form`)}
    >
      <div className="flex flex-col gap-8">
        <p className="text-s13 font-w600 text-text-strong">{HOW_YOU_SENT_IT}</p>
        <RadioTileGroup
          value={way}
          onValueChange={(next) => setWay(next as SubmissionChannel)}
          aria-label={HOW_YOU_SENT_IT}
          className="flex flex-col gap-8"
          testId={`${PREFIX}-way`}
        >
          {ways.map((choice) => (
            <RadioTile
              key={choice.way}
              value={choice.way}
              label={choice.label}
              testId={`${PREFIX}-way-${choice.way}`}
            />
          ))}
        </RadioTileGroup>
      </div>
      <ComplianceDateField
        label={DATE_SENT}
        value={sentOn}
        onChange={setSentOn}
        testId={`${PREFIX}-date`}
      />
      <div className="flex flex-col gap-8">
        <p className="text-s13 font-w600 text-text-strong">{WHAT_YOU_SENT}</p>
        <RadioTileGroup
          value={content}
          onValueChange={(next) => setContent(next === 'other' ? 'other' : 'package')}
          aria-label={WHAT_YOU_SENT}
          className="flex flex-col gap-8"
          testId={`${PREFIX}-what`}
        >
          {packageSha256 !== undefined && (
            <RadioTile
              value="package"
              label={packageAboveLabel(fileCount)}
              testId={`${PREFIX}-what-package`}
            />
          )}
          <RadioTile value="other" label={SOMETHING_DIFFERENT} testId={`${PREFIX}-what-other`} />
        </RadioTileGroup>
        {content === 'other' && (
          <div className="flex flex-col items-start gap-6 pl-28">
            <AttachedDocument artifact={sent} onChange={setSent} testId={`${PREFIX}-sent`} />
          </div>
        )}
      </div>
      <div className="flex flex-col items-start gap-6">
        <p className="text-s13 font-w600 text-text-strong">{PROOF_OF_SENDING_OPTIONAL}</p>
        <p className="text-s12 leading-h15 text-text-tertiary">{proofHintLine(portalName)}</p>
        <AttachedDocument artifact={proof} onChange={setProof} testId={`${PREFIX}-proof`} />
      </div>
      {failure !== null && failure !== undefined && (
        <ErrorMessage testId={`${PREFIX}-failure`}>
          {serverMessage(failure, TRY_AGAIN_LINE)}
        </ErrorMessage>
      )}
      <div className="flex gap-8">
        <Button
          appearance="saas"
          tone="primary"
          size="md"
          type="submit"
          disabled={!ready || pending}
          loading={pending}
          testId={`${PREFIX}-save`}
        >
          {SAVE}
        </Button>
        <Button
          appearance="saas"
          tone="ghost"
          size="md"
          type="button"
          onClick={onCancel}
          testId={`${PREFIX}-cancel`}
        >
          {CANCEL}
        </Button>
      </div>
    </form>
  );
}

/** Step 4's card (frames 79, 80): its words, then the button, the form, or what was recorded. */
function RecordCard({
  number,
  jurisdiction,
  instructions,
  withFee,
  fileCount,
  packageSha256,
  recorded,
  recordedBy,
  canRecord,
  pending,
  failure,
  onSave,
  onReset,
  results,
}: {
  number: number;
  jurisdiction: string;
  instructions: SubmissionInstructions;
  withFee: boolean;
  fileCount: number;
  packageSha256: string | undefined;
  recorded: SubmissionHandoff | undefined;
  recordedBy: string | undefined;
  canRecord: boolean;
  pending: boolean;
  failure: unknown;
  onSave: (body: HandoffRecordBody) => void;
  onReset: () => void;
  results: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="flex flex-col gap-14 px-20 py-20" testId={`${PREFIX}-record`}>
      <div className="flex flex-col gap-8">
        <h2 className="text-s16 font-w600 text-text-strong">
          <span className="text-text-tertiary">{number}.</span> {RECORD_WHAT_YOU_SENT_STEP}
        </h2>
        <p className="text-s13 leading-h15 text-text">{recordStepLine(jurisdiction)}</p>
      </div>
      {recorded !== undefined ? (
        <>
          <p
            className="text-s14 font-w600 leading-h15 text-text-strong"
            {...testIdProp(`${PREFIX}-recorded`)}
          >
            {handoffRecordedLine(recorded, recordedBy)}
          </p>
          {results}
        </>
      ) : !canRecord ? null : open ? (
        <RecordForm
          instructions={instructions}
          withFee={withFee}
          fileCount={fileCount}
          packageSha256={packageSha256}
          pending={pending}
          failure={failure}
          onSave={onSave}
          onCancel={() => {
            onReset();
            setOpen(false);
          }}
        />
      ) : (
        <div>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            onClick={() => setOpen(true)}
            testId={`${PREFIX}-open-form`}
          >
            {RECORD_WHAT_YOU_SENT}
          </Button>
        </div>
      )}
    </Card>
  );
}

// ── The page's body ────────────────────────────────────────────────────────────────────────────

export interface HandoffBodyProps {
  jurisdiction: string;
  instructions: SubmissionInstructions;
  /** The served reason Givmo won't email it (S8 or S5): the lead shows only with one. */
  leadReason: string | undefined;
  files: readonly ShownFile[];
  /** How many checklist items are marked not needed (the record's, once recorded). */
  notNeeded: number | undefined;
  packageSha256: string | undefined;
  fee: SubmissionFeeStatement | undefined;
  /** Who entered the fee on the form, and when (the fee item's answer). */
  feeEnteredBy: string | undefined;
  feeEnteredOn: string | undefined;
  /** The form's checklist page as the preparation serves it (MH9). */
  checklistPage: number | undefined;
  signers: readonly HandoffSigner[];
  factualLine: string | undefined;
  recorded: SubmissionHandoff | undefined;
  recordedBy: string | undefined;
  canRecord: boolean;
  downloading: boolean;
  recording: boolean;
  recordFailure: unknown;
  /** The way back to the review, for a filing Givmo can email (N-H1, approved, D-CCM-40). */
  emailInstead?: ReactNode;
  /**
   * What came back after the organization's own send (CCM-R1-RESULTS-WEB, U1): under frame 81's
   * line, the email card's blocks — what's known now, what happens next, and the list.
   */
  results?: ReactNode;
  onOpen: (file: ShownFile) => void;
  onDownload: () => void;
  onRecord: (body: HandoffRecordBody) => void;
  onResetRecord: () => void;
}

/** A fee to pay with the filing, as the form states it: neither "no fee" nor unknown. */
function hasFee(fee: SubmissionFeeStatement | undefined): boolean {
  return (
    fee !== undefined &&
    (fee.kind === 'organization_pays_agency' || fee.kind === 'amount_unresolved')
  );
}

export function HandoffBody(props: HandoffBodyProps) {
  const {
    jurisdiction,
    instructions,
    leadReason,
    files,
    notNeeded,
    packageSha256,
    fee,
    checklistPage,
  } = props;
  const withFee = hasFee(fee);
  const more = notNeeded !== undefined ? moreNotNeededLine(notNeeded) : undefined;
  const feeNumber = withFee ? 2 : undefined;
  const sendNumber = withFee ? 3 : 2;
  const recordNumber = sendNumber + 1;
  return (
    <div className="grid items-start gap-32 lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className="flex min-w-0 flex-col gap-32">
        {leadReason !== undefined && <HandoffLead reason={leadReason} />}
        <Step number={1} title={GET_THE_PACKAGE} testId={`${PREFIX}-step-package`}>
          {files.length > 0 && (
            <>
              <p className="-mt-4 text-s13 text-text-secondary">
                {handoffPackageLead(files.length, jurisdiction)}
              </p>
              <PackageList files={files} jurisdiction={jurisdiction} onOpen={props.onOpen} />
            </>
          )}
          {more !== undefined && (
            <p
              className="text-s12 leading-h15 text-text-tertiary"
              {...testIdProp(`${PREFIX}-not-needed`)}
            >
              {more}
            </p>
          )}
          {packageSha256 !== undefined && (
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                size="md"
                leadingIcon={<Icon icon={Download} size={16} aria-hidden />}
                loading={props.downloading}
                onClick={props.onDownload}
                testId={`${PREFIX}-download`}
              >
                {DOWNLOAD_THE_PACKAGE}
              </Button>
            </div>
          )}
          <WhoSigns
            signers={props.signers}
            jurisdiction={jurisdiction}
            signerQuote={instructions.signer?.text}
            factualLine={props.factualLine}
          />
          <ComplianceDisclosure surface="handoff" />
        </Step>
        {feeNumber !== undefined && fee !== undefined && (
          <Step number={feeNumber} title={ADD_THE_FEE} testId={`${PREFIX}-step-fee`}>
            {fee.kind === 'organization_pays_agency' && (
              <p className="text-s14 leading-h15 text-text" {...testIdProp(`${PREFIX}-fee`)}>
                {feeEnteredLine(fee, props.feeEnteredBy, props.feeEnteredOn)}
              </p>
            )}
            {instructions.feePayment !== undefined && (
              <SourceLine testId={`${PREFIX}-fee-quote`}>
                {feePaymentLine(jurisdiction, instructions.feePayment, checklistPage)}
              </SourceLine>
            )}
          </Step>
        )}
        <Step number={sendNumber} title={sendItToStep(jurisdiction)} testId={`${PREFIX}-step-send`}>
          <Channels instructions={instructions} jurisdiction={jurisdiction} withFee={withFee} />
          {props.emailInstead}
        </Step>
      </div>
      <div className={`flex min-w-0 flex-col gap-16 ${STICKY_COLUMN_CLASSES}`}>
        <RecordCard
          number={recordNumber}
          jurisdiction={jurisdiction}
          instructions={instructions}
          withFee={withFee}
          fileCount={files.length}
          packageSha256={packageSha256}
          recorded={props.recorded}
          recordedBy={props.recordedBy}
          canRecord={props.canRecord}
          pending={props.recording}
          failure={props.recordFailure}
          onSave={props.onRecord}
          onReset={props.onResetRecord}
          results={props.results}
        />
      </div>
    </div>
  );
}
